import React from 'react';
import {
  Palette,
  Music,
  Dumbbell,
  PartyPopper,
  User,
  Puzzle,
  CalendarDays,
  Sparkles,
  Tablet,
  Gamepad2,
  Film,
  Video,
  Headphones,
  Brain,
  Users as UsersIcon,
  GraduationCap,
} from 'lucide-react';

const activities = [
  { icon: Palette, label: 'Arts and crafts' },
  { icon: Music, label: 'Music and entertainment' },
  { icon: Dumbbell, label: 'Gentle exercise sessions' },
  { icon: PartyPopper, label: 'Social events and celebrations' },
  { icon: User, label: 'One-to-one engagement' },
  { icon: Puzzle, label: 'Dementia-friendly activities' },
];

const programmeHighlights = [
  {
    icon: CalendarDays,
    title: 'Dedicated Activities Coordinator',
    description:
      "Abney Low has a dedicated activities coordinator who builds a fresh monthly calendar of events, tailored to what our residents love. From themed days and celebrations to gentle exercise, music and reminiscence sessions, there is always something on to enrich each resident's day.",
  },
  {
    icon: Sparkles,
    title: 'Sensory Stimulation',
    description:
      'Many of our residents live with dementia, so daily activities are designed to stimulate the senses (taste, smell, sight, hearing and touch), helping minds stay engaged, calm and connected.',
  },
  {
    icon: Tablet,
    title: '40" Sharp Interactive TV',
    description:
      'Abney Low has its very own 40" Sharp interactive TV: a scratch-resistant Android display with up to 10 simultaneous touchpoints, perfect for one-to-one or small-group sessions, especially with low-mobility residents.',
  },
];

const tableUses = [
  { icon: Brain, label: 'Brain training & memory apps' },
  { icon: Gamepad2, label: 'Multi-user collaborative games' },
  { icon: Headphones, label: 'Music, sing-alongs & quizzes' },
  { icon: Film, label: 'Films, documentaries & web browsing' },
  { icon: Video, label: 'Video calls with family & friends' },
  { icon: Dumbbell, label: 'Keep-fit & gentle exercise lessons' },
  { icon: UsersIcon, label: 'Group activities & rehabilitation' },
  { icon: GraduationCap, label: 'Cognitive improvement & sensory aids' },
];

const ActivitiesSection = () => {
  return (
    <section
      id="activities"
      className="py-20 md:py-32 bg-[#FDF6E3]"
      data-testid="activities-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Row */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/8y44buel_IMG_1319.jpeg"
                alt="The welcoming residents' lounge where activities take place"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/20 rounded-full text-[#c9a227] text-sm font-medium mb-6">
              Activities & Lifestyle
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6 leading-tight">
              Supporting Wellbeing Every Day
            </h2>

            <p className="text-slate-700 text-lg mb-8 leading-relaxed">
              At Abney Low Nursing Home we believe in enriching the lives of
              every resident, including those living with dementia, so they
              feel happy, fulfilled and valued. Our programme blends
              recreation, social events and therapeutic activities, all
              tailored to individual interests and abilities.
            </p>

            {/* Activities Grid */}
            <div className="grid grid-cols-2 gap-4">
              {activities.map((activity, index) => {
                const IconComponent = activity.icon;
                return (
                  <div
                    key={activity.label}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`activity-item-${index}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{activity.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Programme Highlights */}
        <div className="mt-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-4">
              A Thoughtful, Person-Centred Programme
            </h3>
            <p className="text-slate-600">
              Every activity is planned to engage the senses, encourage
              connection and bring genuine moments of joy to our residents'
              days.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {programmeHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md border border-slate-100 transition-shadow"
                  data-testid={`programme-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#c9a227]/15 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <h4 className="text-lg font-bold text-[#1e3a5f] mb-3">
                    {item.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interactive Tables Deep-Dive */}
        <div className="mt-16 bg-[#1e3a5f] rounded-3xl p-8 md:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#c9a227]/20 border border-[#c9a227]/40 rounded-full text-[#f0c64a] text-xs font-medium mb-5">
                <Tablet className="w-3.5 h-3.5" />
                Technology that Cares
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-5 leading-tight">
                The Interactive TV: meaningful engagement at the touch of
                a fingertip
              </h3>
              <p className="text-white/80 leading-relaxed mb-4">
                Research into the neuropathology of dementia and the science of
                play shows that, even in the later stages of dementia, three
                powerful experiences remain accessible:
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {['Sensation', 'Relaxation', 'Reminiscence'].map((tag) => (
                  <div
                    key={tag}
                    className="text-center px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-sm font-medium"
                  >
                    {tag}
                  </div>
                ))}
              </div>
              <p className="text-white/80 leading-relaxed text-sm">
                Guided by a carer, residents reach all three through our
                interactive TV: a robust 40" Android-powered display with
                a constantly updated library of brain-training, memory,
                puzzle and entertainment apps.
              </p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-[#f0c64a] uppercase tracking-wide mb-4">
                How we use it
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {tableUses.map((use, index) => {
                  const Icon = use.icon;
                  return (
                    <div
                      key={use.label}
                      className="flex items-start gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-colors"
                      data-testid={`interactive-use-${index}`}
                    >
                      <Icon className="w-5 h-5 text-[#f0c64a] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white/90 leading-snug">{use.label}</span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-6 text-sm text-white/70 italic">
                Together with our wider activities programme, this technology
                helps keep our residents sharp, happy and entertained, every
                day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesSection;
